/**
 * Reading the open message.
 *
 * Office.js is callback-based and predates promises, so each accessor is
 * wrapped. Everything degrades to null rather than throwing: the taskpane can
 * be pinned with no message selected, and a half-populated form beats a blank
 * pane.
 */

export interface OutlookAttachment { name: string; size: number }

export interface OutlookItem {
  subject: string;
  body: string;
  fromEmail: string | null;
  fromName: string | null;
  /** RFC 5322 Message-ID — the value a later reply carries in References. */
  internetMessageId: string | null;
  /** Exchange conversation id. NOT a Message-ID; stored for correlation only. */
  conversationId: string | null;
  attachments: OutlookAttachment[];
}

const BODY_MAX = 20_000; // ample for a description; the RPC caps at 100k anyway

function bodyText(item: Office.MessageRead): Promise<string> {
  return new Promise((resolve) => {
    try {
      item.body.getAsync(Office.CoercionType.Text, (r) => {
        resolve(r.status === Office.AsyncResultStatus.Succeeded ? String(r.value ?? '') : '');
      });
    } catch {
      resolve('');
    }
  });
}

/**
 * Trim the quoted reply/forward tail exactly as the email ingest path does, so
 * an add-in task reads like one made by forwarding to a board address.
 * Signature stripping is left to the server, which already does it.
 */
function stripQuotedTail(text: string): string {
  let t = text ?? '';
  t = t.replace(/\n-{2,}\s*Original Message\s*-{2,}[\s\S]*/i, '');
  t = t.replace(/\n-{3,}\s*Forwarded message\s*-{3,}[\s\S]*/i, '');
  t = t.replace(/\nFrom:\s[\s\S]{0,200}?\nSent:[\s\S]*/, '');
  t = t.replace(/\nOn\s[\s\S]{0,200}?\swrote:[\s\S]*/, '');
  t = t.replace(/(\n\s*>[^\n]*)+\s*$/, '');
  return t.trim();
}

export async function readCurrentItem(): Promise<OutlookItem | null> {
  let item: Office.MessageRead | undefined;
  try {
    item = Office.context?.mailbox?.item as Office.MessageRead | undefined;
  } catch {
    item = undefined;
  }
  if (!item) return null;

  const raw = await bodyText(item);
  const shape = item as unknown as {
    from?: { emailAddress?: string; displayName?: string };
    internetMessageId?: string;
    conversationId?: string;
    attachments?: Array<{ name?: string; size?: number; isInline?: boolean }>;
  };

  return {
    subject: String(item.subject ?? ''),
    body: stripQuotedTail(raw).slice(0, BODY_MAX),
    fromEmail: shape.from?.emailAddress ? String(shape.from.emailAddress).toLowerCase() : null,
    fromName: shape.from?.displayName ? String(shape.from.displayName) : null,
    internetMessageId: shape.internetMessageId ? String(shape.internetMessageId) : null,
    conversationId: shape.conversationId ? String(shape.conversationId) : null,
    // Inline images are attachments to Office but not to a human — a signature
    // logo would otherwise trigger the "forward this email" hint on every mail.
    attachments: (shape.attachments ?? [])
      .filter((a) => a && !a.isInline)
      .map((a) => ({ name: String(a.name ?? 'attachment'), size: Number(a.size ?? 0) })),
  };
}
